import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { eventiBadge, persone } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UtensilsCrossed, Home, Clock, DoorOpen, QrCode, Camera, X, Check, AlertCircle, Activity } from "lucide-react";
import { toast } from "sonner";
import { Html5QrcodeScanner } from "html5-qrcode";
import QrScanner from "@/components/QrScanner";

const SCANNER_MODES = [
  { id: "pasto", label: "Mensa", icon: UtensilsCrossed, color: "bg-orange-500", eventType: "pasto_consumato" },
  { id: "alloggio", label: "Alloggio", icon: Home, color: "bg-blue-500", eventType: "alloggio_utilizzato" },
  { id: "servizio", label: "Servizio", icon: Clock, color: "bg-purple-500", eventType: "servizio_completato" },
  { id: "accesso", label: "Area", icon: DoorOpen, color: "bg-green-500", eventType: "accesso_area" },
];

const PASTI = [
  { value: "colazione", label: "Colazione" },
  { value: "pranzo", label: "Pranzo" },
  { value: "cena", label: "Cena" },
  { value: "spuntino", label: "Spuntino" },
];

const AREE = [
  { value: "mensa", label: "Mensa" },
  { value: "dormitori", label: "Dormitori" },
  { value: "cucina", label: "Cucina" },
  { value: "infermeria", label: "Infermeria" },
  { value: "magazzino", label: "Magazzino" },
  { value: "comando", label: "Comando" },
];

export default function GestioneBadge() {
  const [activeMode, setActiveMode] = useState("pasto");
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [tipoPasto, setTipoPasto] = useState("pranzo");
  const [tipoArea, setTipoArea] = useState("mensa");
  const scannerRef = useRef(null);
  const html5QrcodeScannerRef = useRef(null);

  const queryClient = useQueryClient();

  const { data: eventiOggi } = useQuery({
    queryKey: ['eventi-badge-oggi'],
    queryFn: () => eventiBadge.list({ limit: 50 }),
    refetchInterval: 10000,
  });

  const scanMutation = useMutation({
    mutationFn: async ({ qrCode, tipoEvento, dettagli }) => {
      // First get persona by QR
      const persona = await persone.getByQr(qrCode);
      // Then create event
      return eventiBadge.create({
        persona_id: persona.id,
        tipo_evento: tipoEvento,
        dettagli_evento: dettagli,
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(['eventi-badge']);
      setScanResult({ success: true, data });
      toast.success("Badge registrato con successo!");
    },
    onError: (error) => {
      setScanResult({ success: false, error: error.message });
      toast.error(error.message || "Errore nella scansione");
    },
  });

  useEffect(() => {
    if (scanning && scannerRef.current) {
      // Delay per assicurarsi che il DOM sia pronto
      const initScanner = setTimeout(() => {
        const element = document.getElementById("qr-reader");
        if (!element) return;
        
        html5QrcodeScannerRef.current = new Html5QrcodeScanner(
          "qr-reader",
          { 
            fps: 10, 
            qrbox: { width: 250, height: 250 },
            rememberLastUsedCamera: true,
            showTorchButtonIfSupported: true
          },
          false
        );

        html5QrcodeScannerRef.current.render(
          (decodedText) => {
            handleScan(decodedText);
            stopScanning();
          },
          (error) => {
            // Ignore scan errors
          }
        );
      }, 200);
      
      return () => {
        clearTimeout(initScanner);
        if (html5QrcodeScannerRef.current) {
          html5QrcodeScannerRef.current.clear().catch(() => {});
        }
      };
    }

    return () => {
      if (html5QrcodeScannerRef.current) {
        html5QrcodeScannerRef.current.clear().catch(() => {});
      }
    };
  }, [scanning]);

  const stopScanning = () => {
    if (html5QrcodeScannerRef.current) {
      html5QrcodeScannerRef.current.clear().catch(() => {});
    }
    setScanning(false);
  };

  const handleScan = (qrCode) => {
    const mode = SCANNER_MODES.find(m => m.id === activeMode);
    let dettagli = {};

    if (activeMode === "pasto") {
      dettagli = { tipo_pasto: tipoPasto };
    } else if (activeMode === "accesso") {
      dettagli = { area: tipoArea };
    }

    scanMutation.mutate({
      qrCode,
      tipoEvento: mode.eventType,
      dettagli,
    });
  };

  const startScanning = () => {
    setScanResult(null);
    setScanning(true);
  };

  const currentMode = SCANNER_MODES.find(m => m.id === activeMode);

  const eventiCount = {
    pasto: eventiOggi?.filter(e => e.tipo_evento === "pasto_consumato").length || 0,
    alloggio: eventiOggi?.filter(e => e.tipo_evento === "alloggio_utilizzato").length || 0,
    servizio: eventiOggi?.filter(e => e.tipo_evento === "servizio_completato").length || 0,
    accesso: eventiOggi?.filter(e => e.tipo_evento === "accesso_area").length || 0,
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="gestione-badge-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Gestione Badge</h1>
        <p className="text-slate-500 mt-1">Scanner QR per registrazione eventi</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="badge-stats">
        {SCANNER_MODES.map(mode => (
          <Card key={mode.id} className={`${activeMode === mode.id ? 'ring-2 ring-offset-2' : ''}`} style={{ '--tw-ring-color': mode.id === activeMode ? currentMode.color.replace('bg-', '#') : 'transparent' }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${mode.color}`}>
                  <mode.icon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{eventiCount[mode.id]}</p>
                  <p className="text-xs text-slate-500">{mode.label} Oggi</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Scanner Mode Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Modalità Scanner</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeMode} onValueChange={setActiveMode}>
            <TabsList className="grid w-full grid-cols-4">
              {SCANNER_MODES.map(mode => (
                <TabsTrigger 
                  key={mode.id} 
                  value={mode.id}
                  className="flex items-center gap-2"
                  data-testid={`tab-${mode.id}`}
                >
                  <mode.icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{mode.label}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="pasto" className="mt-4">
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium">Tipo Pasto:</label>
                <Select value={tipoPasto} onValueChange={setTipoPasto}>
                  <SelectTrigger className="w-40" data-testid="select-pasto">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PASTI.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>

            <TabsContent value="alloggio" className="mt-4">
              <p className="text-sm text-slate-600">Scansiona il badge per registrare check-in/check-out alloggio.</p>
            </TabsContent>

            <TabsContent value="servizio" className="mt-4">
              <p className="text-sm text-slate-600">Scansiona il badge per registrare inizio/fine servizio.</p>
            </TabsContent>

            <TabsContent value="accesso" className="mt-4">
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium">Area:</label>
                <Select value={tipoArea} onValueChange={setTipoArea}>
                  <SelectTrigger className="w-40" data-testid="select-area">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AREE.map(a => (
                      <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Scanner */}
      <Card>
        <CardContent className="p-6">
          <QrScanner onScan={(code) => handleScan(code)} onError={(err) => console.error("Scanner error:", err)} />
        </CardContent>
      </Card>

      {/* Result Dialog */}
      <Dialog open={scanResult !== null} onOpenChange={() => setScanResult(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {scanResult?.success ? "Scansione Completata" : "Errore Scansione"}
            </DialogTitle>
          </DialogHeader>
          <div className="text-center py-4">
            {scanResult?.success ? (
              <>
                <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <Check className="h-10 w-10 text-green-600" />
                </div>
                <p className="text-lg font-semibold text-green-600">Badge Registrato!</p>
                {scanResult?.data?.persona && (
                  <p className="text-slate-600 mt-2">
                    {scanResult.data.persona.nome} {scanResult.data.persona.cognome}
                  </p>
                )}
              </>
            ) : (
              <>
                <div className="w-20 h-20 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-4">
                  <AlertCircle className="h-10 w-10 text-red-600" />
                </div>
                <p className="text-lg font-semibold text-red-600">Errore</p>
                <p className="text-slate-600 mt-2">{scanResult?.error}</p>
              </>
            )}
          </div>
          <Button onClick={() => setScanResult(null)} className="w-full">
            Chiudi
          </Button>
        </DialogContent>
      </Dialog>

      {/* Recent Events */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Ultimi Eventi
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {eventiOggi?.slice(0, 20).map(evento => {
              const mode = SCANNER_MODES.find(m => m.eventType === evento.tipo_evento);
              return (
                <div 
                  key={evento.id}
                  className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg"
                >
                  <div className={`p-2 rounded-lg ${mode?.color || 'bg-slate-500'}`}>
                    {mode ? <mode.icon className="h-4 w-4 text-white" /> : <QrCode className="h-4 w-4 text-white" />}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm">
                      {evento.persona?.nome} {evento.persona?.cognome}
                    </p>
                    <p className="text-xs text-slate-500">
                      {evento.tipo_evento.replace("_", " ")} 
                      {evento.dettagli_evento?.tipo_pasto && ` - ${evento.dettagli_evento.tipo_pasto}`}
                      {evento.dettagli_evento?.area && ` - ${evento.dettagli_evento.area}`}
                    </p>
                  </div>
                  <span className="text-xs text-slate-400">
                    {new Date(evento.timestamp_evento).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              );
            })}
            {(!eventiOggi || eventiOggi.length === 0) && (
              <p className="text-center text-slate-500 py-8">Nessun evento registrato oggi</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
